import { action, observable, makeObservable, runInAction, computed } from 'mobx'
import i18next from 'i18next'
import AsyncStore from 'stores/AsyncStore'
import AccidentService from 'services/CustomerSelfManagementService/AccidentService'
import Accident from 'models/Accident'
import Insured from 'models/Accident/Insured'
import Driver from 'models/Accident/Driver'
import ThirdParties from 'models/Accident/ThirdParties'
import {
    ACCIDENT_STEPS,
    VEHICLE_ACCIDENT_TYPE,
    STATUS,
    BLOCK_NEW_ACCIDENT_LOAD_STATUS_IDS,
    isVehicleRiskType,
} from 'util/accidentConstants'

const mapGroupedAccidentOption = (option, accident) => ({
    description: option.description,
    value: option.description,
    insurance: option.insurance,
    policyIds: option.policyIds,
    policyId: accident.policyId,
    accidentId: accident.accidentId,
    status: accident.status,
    createdAt: accident.createdAt,
    asset: {
        description: option.description,
        insurance: option.insurance,
        type: option.insurance,
        insuranceCompany: accident.insuranceCompany,
        policyNumber: accident.policyNumber,
        validFrom: accident.validFrom,
        validTo: accident.validTo,
        plate: accident.plate,
    },
})

class AccidentStore extends AsyncStore {
    constructor(parentStore = null) {
        super()
        this.parentStore = parentStore
        this.accidentService = new AccidentService()

        this.accident = new Accident()
        this.insured = new Insured()
        this.driver = new Driver()
        this.thirdParties = new ThirdParties()

        this.accidentTypes = null
        this.insuranceCompanies = null
        this.accidentOptions = []
        this.accidentSelected = { policyId: null, accidentId: null, value: null }
        this.entryFormExtraInputs = false

        this.userManualURL = ''
        this.filesToSend = []

        this.makeObservables()
    }

    async getInsuranceCompanies() {
        this.insuranceCompanies = await this.accidentService.getInsuranceCompanies()
    }

    async getAccidentTypes() {
        this.accidentTypes = await this.accidentService.getAccidentTypes()
    }

    async getAccidentOptions(identityNumber, { plate, policyNumber } = {}) {
        this.preRequest(this.getAccidentOptions)

        try {
            const options = await this.accidentService.getInsuredPolicies(identityNumber, {
                plate,
                policyNumber,
            })

            runInAction(() => {
                this.setAccidentOptions(options)
                this.onSuccessRequest()
            })
        } catch (e) {
            this.handleErrors(e)
        }
    }

    async initializeAccident() {
        this.preRequest(this.initializeAccident)
        await this.insured.validate()

        if (this.insured.identityNumber.error) {
            this.finishRequest()
            return
        }

        try {
            this.setAccident()

            const { identityNumber } = this.insured.json()
            const { policyId, policyIds, accidentId } = this.accidentSelected
            const { data } = await this.accidentService.getAccident({
                identityNumber,
                policyId,
                policyIds,
                accidentId,
            })

            this.fromAPI(data?.data)

            runInAction(() => {
                this.onSuccessRequest()
            })
        } catch (e) {
            if (e.response?.status === 409) {
                this.setEntryFormExtraInputs(true)
            } else {
                this.setEntryFormExtraInputs(false)
            }

            this.handleErrors(e)
        }
    }

    async updateAccident(step) {
        this.preRequest(this.updateAccident)

        try {
            switch (step) {
                case ACCIDENT_STEPS.ACCIDENT_FORM:
                    await this.accident.validate()
                    if (this.formAccidentHasError) {
                        this.finishRequest()
                        return false
                    }
                    break
                case ACCIDENT_STEPS.DRIVER_FORM:
                    await this.driver.validate()
                    if (this.formDriverHasError) {
                        this.finishRequest()
                        return false
                    }
                    break
                case ACCIDENT_STEPS.THIRD_PARTIES_FORM:
                    await this.thirdParties.validate()
                    if (this.formThirdPartiesHasError){
                        this.finishRequest()
                        return false
                    }
                    break
                case ACCIDENT_STEPS.DOCS_FORM: {
                    const hasInvalidFiles = this.accident.filesForm?.files?.some((file) => (
                        file?.error || (file?.file !== null && !file?.fileBase64)
                    ))

                    if (hasInvalidFiles) {
                        this.finishRequest()
                        return false
                    }

                    const files = this.accident.filesForm?.files.map((file) => {
                        if (file.file !== null) {
                            return { filename: file.file?.name, content: file.fileBase64 }
                        }
                        return null
                    })
                        .filter((document) => document !== null)

                    this.setFilesToSend(files)
                    this.accident.setDocuments(files.length)
                    break
                }
                case ACCIDENT_STEPS.REVIEW_FORM:
                    await this.accident.validate()

                    if (!this.accident.email.value || this.accident.email.value.trim() === '') {
                        this.accident.email.setError(i18next.t('accidents:emailRequired'))
                        this.finishRequest()
                        return false
                    }

                    if (this.accident.email.error) {
                        this.finishRequest()
                        return false
                    }

                    await this.accidentService.sendDocuments({
                        context: 'accident',
                        identityNumber: this.insured?.identityNumber?.value,
                        accidentId: this.accident.id,
                        files: this.filesToSend
                    })

                    if (this.accident.status?.id === STATUS.DRAFT || this.accident.status?.id === STATUS.SENDING) {
                        this.accident.setStatus( {id: STATUS.SENDING, value: "Enviando a Pasap"} )
                    }
                    break
                case ACCIDENT_STEPS.EXTENSION_FORM: {
                    await this.accident.validate()
                    if (this.accident.extensionNote.error) {
                        this.finishRequest()
                        return false
                    }

                    const extensionFiles = this.accident.extensionsFilesForm?.files.map((file) => {
                        if (file.file !== null) {
                            return { filename: file.file?.name, content: file.fileBase64 }
                        }
                        return null
                    })
                        .filter((document) => document !== null)

                    await this.accidentService.addExtension({
                        identityNumber: this.insured?.identityNumber?.value,
                        accidentId: this.accident.id,
                        note: this.accident.extensionNote?.value,
                        files: extensionFiles,
                    })
                    break
                }
                default:
                    break
            }

            this.setAccident()
            const { data } = await this.accidentService.saveAccident(this.accident.json())

            if (this.accident.id === null) {
                this.accident.setIdAccident(data?.data.id)
            }

            runInAction(() => {
                this.onSuccessRequest()
                this.finishRequest()
            })

            return true
        } catch (e) {
            return this.handleErrors(e)
        }
    }

    setAccident() {
        this.accident.setInsured(this.insured)
        this.accident.setDriver(this.driver)
        this.accident.setThirdParties(this.thirdParties)
    }

    setThirdParties(value) {
        this.thirdParties = value
    }

    setEntryFormExtraInputs(value) {
        this.entryFormExtraInputs = value
    }

    setUserManualURL(value) {
        this.userManualURL = value
    }

    setInsuredAsDriver() {
        if (this.driver.isInsured) {
            this.driver = new Driver()
            this.setAccident()
        } else {
            const { birthDate, fullName, identityNumber } = this.insured

            this.driver.setBirthDate(birthDate.value)
            this.driver.setFullName(fullName.value)
            this.driver.setIdentityNumber(identityNumber.value)
            this.driver.isInsured = true
            this.setAccident()
        }
    }

    resetAccidentOptions() {
        this.accidentOptions = []
        this.accidentSelected = { policyId: null, accidentId: null, value: null }
    }

    setAccidentSelected(accident) {
        this.accidentSelected = accident
    }

    setFilesToSend(files) {
        this.filesToSend = files
    }

    setAccidentOptions(value) {
        this.accidentOptions = value
    }

    get createAccidentOptions() {
        return this.accidentOptions
            .filter((option) => {
                if (!Array.isArray(option?.policyIds) || option.policyIds.length === 0) {
                    return false
                }

                const accidents = option.accidents || []

                if (accidents.length === 0) {
                    return true
                }

                return !accidents.some((accident) => (
                    BLOCK_NEW_ACCIDENT_LOAD_STATUS_IDS.has(accident?.status?.id)
                ))
            })
            .map((option) => ({
                ...option,
                policyId: null,
                accidentId: null,
                status: null,
            }))
    }

    get continueAccidentOptions() {
        return this.accidentOptions.flatMap((option) => (
            (option.accidents || [])
                .filter((accident) => accident?.status?.id === STATUS.DRAFT)
                .map((accident) => mapGroupedAccidentOption(option, accident))
        ))
    }

    get previousAccidentOptions() {
        return this.accidentOptions.flatMap((option) => (
            (option.accidents || [])
                .filter((accident) => (
                    accident?.status?.id
                    && accident.status.id !== STATUS.DRAFT
                ))
                .map((accident) => mapGroupedAccidentOption(option, accident))
        ))
    }

    get accidentPolicyHeaderSource() {
        const selectedAsset = this.accidentSelected?.asset
        const accidentAsset = this.accident?.asset
        const company = selectedAsset?.insuranceCompany
            || selectedAsset?.insuredCompany
            || this.accident?.insuredCompany
            || null
        const assetDescription = selectedAsset?.description
            || this.accidentSelected?.description
            || (typeof accidentAsset === 'string' ? accidentAsset : accidentAsset?.description)
            || null
        const policyNumber = selectedAsset?.policyNumber || null
        const validFrom = selectedAsset?.validFrom || null
        const validTo = selectedAsset?.validTo || null
        const plate = selectedAsset?.plate || this.accident?.plate || null

        if (!company && !assetDescription && !policyNumber && !plate) {
            return null
        }

        return {
            insuranceCompany: company,
            riskType: selectedAsset?.riskType || this.accident?.riskType || null,
            asset: assetDescription,
            policyNumber,
            validFrom,
            validTo,
            plate,
            insuredName: null,
        }
    }

    syncSelectedAccidentAsset(accidentId = this.accidentSelected?.accidentId || this.accident?.id) {
        if (!accidentId) {
            return
        }

        const match = [
            ...this.previousAccidentOptions,
            ...this.continueAccidentOptions,
        ].find((option) => option?.accidentId === accidentId)

        if (!match) {
            return
        }

        this.setAccidentSelected({
            ...this.accidentSelected,
            policyId: match.policyId || this.accidentSelected?.policyId || this.accident?.policyId,
            policyIds: match.policyIds || this.accidentSelected?.policyIds || null,
            accidentId,
            status: match.status || this.accidentSelected?.status || this.accident?.status,
            value: match.description || match.value || this.accidentSelected?.value,
            description: match.description || this.accidentSelected?.description,
            insurance: match.insurance || this.accidentSelected?.insurance || null,
            asset: match.asset || this.accidentSelected?.asset || null,
        })
    }

    async getUserManualURL() {
        this.preRequest(this.getUserManualURL)

        try {
            const response = await this.accidentService.getUserManual()

            runInAction(() => {
                this.setUserManualURL(response)
                this.onSuccessRequest()
            })
        } catch (e) {
            runInAction(() => {
                this.onErrorRequest(e)
            })
        }
    }

    async handleErrors(e) {
        if (e.response?.status === 404) {
            this.onErrorRequest(i18next.t('accidents:error404'))
        } else if (e.response?.status === 409) {
            this.onErrorRequest(e.response?.data?.error?.message)
            await this.getAccidentOptions(this.insured.identityNumber?.value, {
                plate: this.parentStore?.plate?.value,
                policyNumber: this.parentStore?.policyNumberInput?.value
                    || this.parentStore?.policyNumber,
            })
        } else if (e.response?.status === 400) {
            this.onErrorRequest(e.response?.data?.error?.message)
        } else if (e.response?.status === 500) {
            this.onErrorRequest(i18next.t('accidents:error500'))
        } else if (e.status === 401) {
            this.onErrorRequest('')
        } else {
            this.onErrorRequest(i18next.t('accidents:genericError'))
        }

        this.finishRequest()
        return false
    }

    get isVehicle() {
        return isVehicleRiskType(this.accident?.riskType)
    }

    get hasThirdParties() {
        return (
            this.accident?.vehicleAccidentType?.value?.id === VEHICLE_ACCIDENT_TYPE.CRASH_WITH_INJURIES ||
            this.accident?.vehicleAccidentType?.value?.id === VEHICLE_ACCIDENT_TYPE.CRASH
        )
    }

    get imagesUploaded() {
        return (
            this.accident.documents?.images?.length
        )
    }

    get pdfsUploaded() {
        return (
            this.accident.documents?.pdfs?.length
        )
    }

    get extensionImagesUploaded() {
        return (
            this.accident.newExtension?.documents?.images?.length
        )
    }

    get extensionPdfsUploaded() {
        return (
            this.accident.newExtension?.documents?.pdfs?.length
        )
    }

    get hasDocumentsUploaded() {
        return (
            this.accident.documents > 0
        )
    }

    get hasReplacementLocation() {
        return (
            this.accident.vehicleAccidentType?.value?.id === VEHICLE_ACCIDENT_TYPE.CRYSTALS ||
            this.accident.vehicleAccidentType?.value?.id === VEHICLE_ACCIDENT_TYPE.LOCKS ||
            this.accident.vehicleAccidentType?.value?.id === VEHICLE_ACCIDENT_TYPE.WHEEL
        )
    }

    get formAccidentHasError() {
        return (
            this.accident.date.error ||
            this.accident.time.error ||
            this.accident.description.error ||
            this.accident.place.error ||
            this.accident.damages.error ||
            (this.isVehicle && this.accident.vehicleAccidentType.error)
        )
    }

    get formDriverHasError() {
        return (
            this.driver.identityNumber.error ||
            this.driver.fullName.error
        )
    }

    get formThirdPartiesHasError() {
        return (
            this.thirdParties.identityNumber.error ||
            this.thirdParties.plate.error
        )
    }

    get platform() {
        return this.parentStore?.platform ?? null
    }

    get colors() {
        return this.parentStore?.colors ?? null
    }

    get identityNumber() {
        return this.parentStore?.identityNumber?.value ?? null
    }

    json() {
        return {
            accident: this.accident?.json(),
            insured: this.insured?.json(),
            driver: this.driver?.json(),
            thirdParties: this.thirdParties?.json(),
        }
    }

    fromAPI(data) {
        this.accident.fromAPI(data)
        this.insured.fromAPI(data.insured)
        this.driver.fromAPI(data.driver)
        this.thirdParties.fromAPI(data.thirdParties)
        this.setAccident()
    }

    reset() {
        this.accident = new Accident()
        this.insured = new Insured()
        this.driver = new Driver()
        this.thirdParties = new ThirdParties()
    }

    makeObservables() {
        makeObservable(this, {
            accident: observable,
            insured: observable,
            driver: observable,
            thirdParties: observable,
            accidentTypes: observable,
            insuranceCompanies: observable,
            entryFormExtraInputs: observable,
            accidentOptions: observable,
            accidentSelected: observable,
            userManualURL: observable,
            createAccidentOptions: computed,
            continueAccidentOptions: computed,
            previousAccidentOptions: computed,
            accidentPolicyHeaderSource: computed,
            initializeAccident: action,
            getInsuranceCompanies: action,
            getAccidentTypes: action,
            reset: action,
            setInsuredAsDriver: action,
            setEntryFormExtraInputs: action,
            setAccidentOptions: action,
            getAccidentOptions: action,
            resetAccidentOptions: action,
            setAccidentSelected: action,
            syncSelectedAccidentAsset: action,
            setUserManualURL: action,
        })
    }
}

export default AccidentStore
